/**
 * AdminFormsPreviewMigration — TEMPORARY admin-only control.
 *
 * One-off tool (2026-08-30) to copy the 26 Forms Library templates built
 * on Preview into Production, which never received them because the two
 * environments' databases are entirely separate. See
 * backend/forms.py::admin_migrate_forms_from_preview. Dry-run first, real
 * run only after review.
 *
 * Safe to delete this file + its route/AdminHome card, the backend
 * endpoint and data/forms_migration_source_preview_2026-08-30.json once
 * the migration has been run and confirmed in Production.
 */
import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";

function Row({ items, empty }) {
  if (!items || items.length === 0) return <p className="text-sm text-ink-soft">{empty}</p>;
  return (
    <ul className="mt-1 space-y-1 text-sm text-ink">
      {items.map((it, i) => (
        <li key={i}>
          <span className="font-medium">{it.title}</span>
          {it.status && <span className="text-ink-soft"> · {it.status}</span>}
          {it.reason && <span className="text-amber-700"> — {it.reason}</span>}
          {it.new_template_id && <span className="text-ink-soft font-mono text-xs"> · {it.new_template_id}</span>}
        </li>
      ))}
    </ul>
  );
}

export default function AdminFormsPreviewMigration() {
  const [dryRunResult, setDryRunResult] = useState(null);
  const [realResult, setRealResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmingReal, setConfirmingReal] = useState(false);
  const [error, setError] = useState("");

  const runDryRun = async () => {
    setLoading(true);
    setError("");
    setRealResult(null);
    try {
      const { data } = await api.post("/api/admin/forms/migrate-from-preview?dry_run=true");
      setDryRunResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const runReal = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/api/admin/forms/migrate-from-preview?dry_run=false");
      setRealResult(data);
      setConfirmingReal(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-forms-preview-migration-page">
      <PageHero
        eyebrow="JCC Admin — temporary tool"
        title="Forms: Preview → Production migration"
        description="One-off tool to copy the Forms Library templates built on Preview into Production. Only ever creates brand-new templates — never updates or overwrites anything already in Production (including the pilot form and Client Enquiry Form). Run the dry run first."
      />
      <section className="section pt-8">
        <div className="container-jcc max-w-3xl space-y-6">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="admin-forms-migration-error">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={runDryRun}
            disabled={loading}
            className="btn-outline"
            data-testid="admin-forms-migration-dry-run-btn"
          >
            {loading ? "Running dry run…" : "Run dry-run migration"}
          </button>

          {dryRunResult && (
            <div className="card-base p-6 space-y-4" data-testid="admin-forms-migration-dry-run-result">
              <p className="text-sm text-ink-muted">Source: {dryRunResult.source_total} templates exported from Preview.</p>
              <div>
                <p className="font-semibold text-ink" data-testid="admin-forms-migration-would-create-count">
                  Would create: {dryRunResult.would_create_count}
                </p>
                <Row items={dryRunResult.would_create} empty="None." />
              </div>
              <div>
                <p className="font-semibold text-amber-700" data-testid="admin-forms-migration-skipped-count">
                  Skipped / flagged: {dryRunResult.skipped_count}
                </p>
                <Row items={dryRunResult.skipped} empty="None." />
              </div>

              {!realResult && !confirmingReal && (
                <button
                  type="button"
                  onClick={() => setConfirmingReal(true)}
                  className="btn-purple"
                  data-testid="admin-forms-migration-run-real-btn"
                >
                  Run migration
                </button>
              )}

              {confirmingReal && !realResult && (
                <div className="rounded-xl border border-purple/30 bg-purple/5 p-4 space-y-3" data-testid="admin-forms-migration-confirm-box">
                  <p className="text-sm text-ink">
                    This will create {dryRunResult.would_create_count} new form template(s) in Production,
                    matching the statuses (draft/published) shown above. Nothing existing is changed.
                  </p>
                  <div className="flex gap-3">
                    <button type="button" onClick={runReal} disabled={loading} className="btn-purple" data-testid="admin-forms-migration-confirm-btn">
                      {loading ? "Running…" : "Yes, run the real migration"}
                    </button>
                    <button type="button" onClick={() => setConfirmingReal(false)} className="btn-outline" data-testid="admin-forms-migration-cancel-btn">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {realResult && (
            <div className="card-base p-6 space-y-3" data-testid="admin-forms-migration-real-result">
              <p className="font-semibold text-teal" data-testid="admin-forms-migration-created-count">
                Created: {realResult.created_count}
              </p>
              <Row items={realResult.created} empty="None." />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
